import { registerEventHandler } from 'reffects';
import { http } from 'reffects-batteries';
import cache from 'effects/cache';

/**
 * @param {string} cacheDomain
 * @param {Function} composeCacheKeyFromPayload
 * @param {Function} prepareHttpGetEffectFromPayload
 * @param {Function} parseItemFromHttpResponse
 * @param {Function} itemHandler
 * @param {object} cacheConfig
 * @returns {{retrieve: string, reload: string}}
 */
export function registerHttpCacheEvents(
    cacheDomain,
    composeCacheKeyFromPayload,
    prepareHttpGetEffectFromPayload,
    parseItemFromHttpResponse,
    itemHandler,
    cacheConfig
) {
    const retrieveEventName = `${cacheDomain}_CACHE.RETRIEVE`;
    const reloadEventName = `${cacheDomain}_CACHE.RELOAD_FROM_SOURCE`;
    const cacheHitEventName = `${cacheDomain}_CACHE.HIT`;
    const cacheMissEventName = `${cacheDomain}_CACHE.MISS`;
    const loadedFromSourceCacheName = `${cacheDomain}_CACHE.LOADED_FROM_SOURCE`;
    const loadFromSourceFailedEventName = `${cacheDomain}_CACHE.LOAD_FROM_SOURCE_FAILED`;

    registerEventHandler(retrieveEventName, function retrieveItem(_, payload) {
        return {
            ...cache.get({
                key: composeCacheKeyFromPayload(payload),
                hitEvent: cacheHitEventName,
                missEvent: cacheMissEventName,
                extraPayload: payload,
            }),
        };
    });

    registerEventHandler(reloadEventName, function reloadItem(_, payload) {
        return http.get({
            ...prepareHttpGetEffectFromPayload(payload),
            successEvent: [loadedFromSourceCacheName, payload],
            errorEvent: [loadFromSourceFailedEventName, payload],
        });
    });

    registerEventHandler(
        cacheHitEventName,
        function itemCacheHit(_, { value: item, extra: retrievePayload }) {
            return itemHandler(item, retrievePayload);
        }
    );

    registerEventHandler(
        cacheMissEventName,
        function itemCacheMiss(_, { extra: retrievePayload }) {
            return http.get({
                ...prepareHttpGetEffectFromPayload(retrievePayload),
                successEvent: [loadedFromSourceCacheName, retrievePayload],
                errorEvent: [loadFromSourceFailedEventName, retrievePayload],
            });
        }
    );

    registerEventHandler(
        loadedFromSourceCacheName,
        function itemLoadedFromSource(_, [response, cachePayload]) {
            return {
                ...itemHandler(
                    parseItemFromHttpResponse(response, cachePayload),
                    cachePayload
                ),
                ...cache.set({
                    key: composeCacheKeyFromPayload(cachePayload),
                    value: parseItemFromHttpResponse(response, cachePayload),
                    config: cacheConfig,
                }),
            };
        }
    );

    registerEventHandler(
        loadFromSourceFailedEventName,
        function itemLoadFromSourceFailed() {
            return {};
        }
    );

    return { retrieve: retrieveEventName, reload: reloadEventName };
}

export default function registerCacheEvents() {
    registerEventHandler('CLEAR_CACHE', function clearCacheEvents() {
        return cache.clear();
    });
}
